import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, input, OnInit, output, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { CommonLocalizationModule, HashMap, LocalizationService } from '@capital-access/common/localization';
import {
  FireflyFormattingModule,
  FireflySuggesterModule,
  FireflyTooltipModule,
  SuggesterValidation
} from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../../common-components.const';
import { SecuritySuggesterItem, SecuritySuggesterItemType } from './models/security-suggester-item.model';
import { SecurityMultiSuggesterStore } from './security-multi-suggester.store';

@Component({
  selector: 'ca-security-multi-suggester',
  standalone: true,
  imports: [
    CommonModule,
    FireflyFormattingModule,
    FireflySuggesterModule,
    FireflyTooltipModule,
    CommonLocalizationModule,
    ReactiveFormsModule
  ],
  templateUrl: './security-multi-suggester.component.html',
  styleUrls: ['./security-multi-suggester.component.scss'],
  providers: [SecurityMultiSuggesterStore]
})
export class SecurityMultiSuggesterComponent implements OnInit {
  readonly securityStore = inject(SecurityMultiSuggesterStore);
  securityLists = input.required<SecuritySuggesterItem[]>();
  primarySecurityListId = input.required<number | null>();
  maxSelectionLimit = input.required<number>();
  placeholder = input.required<string>();
  initialSelections = input<SecuritySuggesterItem[]>([]);
  @Input() validation?: SuggesterValidation;
  onSelectedSuggesterItems = output<SecuritySuggesterItem[]>();
  focus = output<void>();
  outclick = output<void>();

  suggesterItemType = SecuritySuggesterItemType;
  localizationScope = COMMON_COMPONENTS_LOCALIZATION_SCOPE;

  getLocalization = toSignal(inject(LocalizationService).getLocalization(COMMON_COMPONENTS_LOCALIZATION_SCOPE), {
    initialValue: {} as HashMap
  });

  constructor() {
    effect(() => {
      const initialSelectedItems = this.initialSelections();
      untracked(() => {
        this.updateSelection(initialSelectedItems);
      });
    });
  }

  ngOnInit() {
    this.securityStore.setInitialData(this.securityLists(), this.maxSelectionLimit());
  }

  onSelect(item: SecuritySuggesterItem) {
    this.updateSelection([...(this.securityStore.selectedItems() ?? []), item]);
  }

  onSelectMany(items: SecuritySuggesterItem[]) {
    this.updateSelection([...(this.securityStore.selectedItems() ?? []), ...items]);
  }

  onRemove(item: SecuritySuggesterItem) {
    this.updateSelection(this.securityStore.selectedItems()!.filter(selected => selected.id !== item.id));
  }

  onRemoveMany(items: SecuritySuggesterItem[]) {
    const idsToRemove = items.map(i => i.id);
    this.updateSelection(this.securityStore.selectedItems()!.filter(selected => !idsToRemove.includes(selected.id)));
  }

  updateSelection(selectedItems: SecuritySuggesterItem[]) {
    this.securityStore.onSelectionChange(selectedItems);
    this.onSelectedSuggesterItems.emit(selectedItems);
  }
}
